import React, { useEffect, useRef, useState } from "react";
import Button from "@amzn/meridian/esm/components/button";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import Column from "@amzn/meridian/esm/components/column";
import Icon from "@amzn/meridian/esm/components/icon";
import Popover from "@amzn/meridian/esm/components/popover";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Textarea from "@amzn/meridian/esm/components/textarea";
import Theme from "@amzn/meridian/esm/components/theme/theme";

import LogManager from "../../utils/logManager";
import aimsTheme from "../../styles/meridian-theme-aims.json";
import FeedbackPopoverTop from "../../images/feedback_popover_top.svg";
import feedbackNotSubmitted from "../../images/feedback_not_submitted.svg";
import feedbackSubmitted from "../../images/feedback_submitted.svg";
import { FEEDBACK_MESSAGE, FEEDBACK_TYPE } from './feedback.constants';

/**
 * Feedback component for no meeting summary.
 * Handles user feedback submission and API interaction.
 *
 * @param {string} aimsUuid - UUID for the meeting summary
 * @returns {JSX.Element} No Summary feedback UI Popover
 */

const SummaryNotApprovedFeedback = ({ aimsUuid }) => {
    const popoverRef = useRef()
    const [feedbackComment, setFeedbackComment] = useState("");
    const [popoverOpen, setPopoverOpen] = useState(true);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isRefReady, setIsRefReady] = useState(false);

    const onClosePopover = () => {
        setPopoverOpen(false);
        setFeedbackComment("");
        setIsSubmitted(false); // Reset the submitted state when pop over is closed
    };

    const submitFeedback = async () => {
        LogManager.info("Calling /feedback API with aimsUuid: " + aimsUuid);
        try {
            const response = await fetch('/feedback', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    type: FEEDBACK_TYPE.SUMMARY,
                    aimsUuid: aimsUuid,
                    feedbackTimestamp: Math.floor(Date.now() / 1000),
                    feedbackComment: feedbackComment
                })
            });
            const responseJson = await response.json();
            LogManager.info("The response of /feedback is:", responseJson);

            //TODO error handling for statusCode
            const statusCode = responseJson.StatusCode;
        } catch (error) {
            LogManager.error('Error submitting feedback:', error);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitted(true);
        feedbackComment && await submitFeedback();
    };

    useEffect(() => {
        // Once the ref is available, set isRefReady to true
        if (popoverRef.current) {
            setIsRefReady(true);
        }
    }, []);

    return (
        <>
            <Row height="70px"/>
            <Row ref={popoverRef}/>
            {popoverOpen && isRefReady &&
                <Theme palette={aimsTheme}>
                    <Popover
                        open={true}
                        minWidth="292px"
                        anchorNode={popoverRef.current}
                        onClose={()=>{}}
                        position="top"
                        indicatorArrow={false}
                        spacingInset="none"
                    >
                        <Column heights={["20%","60%","20%"]} height="270px" spacing="none">
                            {isSubmitted ? 
                                <>
                                    <div                     
                                        style={{
                                            background: `url(${FeedbackPopoverTop}) no-repeat center/cover`,
                                            width: "100%",
                                        }}
                                    >
                                        <Row widths={["80%","20%"]} alignmentVertical="bottom" height="50px">
                                            <div/>
                                            <Button type="icon" onClick={onClosePopover}>
                                                <Icon tokens={closeSmallTokens}/>
                                            </Button>
                                        </Row>
                                    </div>
                                    <Column alignmentHorizontal="center" alignmentVertical="center">
                                        {feedbackComment ? 
                                        <>
                                            <img
                                                src={feedbackSubmitted}
                                                style={{width: '100px', height: 'auto', maxWidth: '100%'}}
                                            />
                                            <Text type="h50">
                                                {FEEDBACK_MESSAGE.SUBMITTED}
                                            </Text>
                                        </>
                                        :
                                        <>
                                            <img
                                                src={feedbackNotSubmitted}
                                                style={{width: '60px', height: 'auto', maxWidth: '100%'}}
                                            />
                                            <Text type="h50">
                                                {FEEDBACK_MESSAGE.NOT_SUBMITTED}
                                            </Text>
                                        </>
                                        }
                                    </Column>
                                    <Row alignmentHorizontal="end" spacingInset="none 500 none none">
                                        <Button type="primary" size="small" minWidth="90px" onClick={onClosePopover}>Close</Button>
                                    </Row>
                                </>
                            :
                                <>
                                    <div                     
                                        style={{
                                            background: `url(${FeedbackPopoverTop}) no-repeat center/cover`,
                                            width: "100%",
                                        }}
                                    >
                                        <Row widths={["10%", "70%","20%"]} alignmentVertical="bottom" height="50px">
                                            <div/>
                                            <Text type="h100">
                                                Share Feedback
                                            </Text>
                                            <Button type="icon" onClick={onClosePopover}>
                                                <Icon tokens={closeSmallTokens}/>
                                            </Button>
                                        </Row>
                                    </div>
                                    <Row alignmentHorizontal="center">
                                        <Textarea
                                            value={feedbackComment}
                                            onChange={setFeedbackComment}
                                            placeholder="Enter comment"
                                            error={false}
                                            width="230px"
                                            rows={3}
                                            size="small"
                                        />
                                    </Row>
                                    <Row alignmentHorizontal="end" spacingInset="none 500 none none">
                                        <Button type="primary" size="small" minWidth="90px" onClick={handleSubmit}>
                                            Submit
                                        </Button>
                                    </Row>
                                </>
                            }
                        </Column>
                    </Popover>
                </Theme>
            }
        </>
    );
};

export default SummaryNotApprovedFeedback;